import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import theme from "theme";

const CaptionBar = styled.div`
	flex-shrink: 0;
	margin-top: 0.65rem;
	padding-top: 0.55rem;
	border-top: 1px solid rgba(111, 131, 159, 0.35);
	min-height: 2.65rem;
	display: flex;
	flex-direction: column;
	gap: 0.35rem;
`;

const Caption = styled.p`
	margin: 0;
	font-family: system-ui, -apple-system, sans-serif;
	font-size: clamp(0.7rem, 1.9vw, 0.8125rem);
	line-height: 1.35;
	color: #c5d4e0;
	text-align: center;
`;

const SceneDots = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	gap: 0.35rem;
`;

const SceneDot = styled.span`
	width: ${(p) => (p.$on ? "18px" : "6px")};
	height: 6px;
	border-radius: 999px;
	background: ${(p) => (p.$on ? theme.color.primary : "rgba(111, 131, 159, 0.45)")};
	transition: width 0.7s ease, background 0.7s ease;
`;

const Wrap = styled.div`
	width: 100%;
	max-width: 100%;
	flex: 1;
	min-height: 0;
	height: 100%;
	display: flex;
	flex-direction: column;
	overflow: hidden;

	.hero-scene-row {
		/* Opacity only — transitioning transform desyncs rows from the trail/marker,
		 * which update immediately when rowDy changes with panel height. */
		transition: opacity 0.9s ease;
	}

	.flow-line {
		stroke-dasharray: 5 5;
		animation: intentFlow 28s linear infinite;
	}

	@keyframes intentFlow {
		to {
			stroke-dashoffset: -120;
		}
	}

	.story-marker-pulse {
		animation: markerPulse 2.2s ease-in-out infinite;
	}

	@keyframes markerPulse {
		0%,
		100% {
			opacity: 0.35;
			r: 7;
		}
		50% {
			opacity: 0.85;
			r: 10;
		}
	}

	@media (prefers-reduced-motion: reduce) {
		.hero-scene-row {
			transition: none;
		}
		.flow-line {
			animation: none;
			stroke-dasharray: none;
		}
		.story-marker-pulse {
			animation: none;
		}
	}
`;

const DiagramSlot = styled.div`
	flex: 1 1 auto;
	min-height: 0;
	width: 100%;
	display: flex;
	align-items: stretch;

	svg {
		display: block;
		width: 100%;
		height: 100%;
		min-height: 0;
	}
`;

/** 10px gap between successive row bottoms; minimal viewBox padding (2px top, 4px bottom). */
const GAP = 10;
const Y1 = 2;
const R1_BOTTOM = Y1 + 38;
const Y2 = R1_BOTTOM + GAP;
const R2_BOTTOM = Y2 - 2 + 40;
const Y3 = R2_BOTTOM + GAP;
const R3_BOTTOM = Y3 - 4 + 52;
const Y4 = R3_BOTTOM + GAP;
const R4_BOTTOM = Y4 + 28;
const Y5 = R4_BOTTOM + GAP + 12;
const R5_BOTTOM = Y5 + 36;
const Y6 = R5_BOTTOM + GAP;
const R6_BOTTOM = Y6 + 32;
const VIEW_H = R6_BOTTOM + 4;
const ACTION_BOX_Y_OFFSET = 4;
const INTEGRATE_DEST_Y_OFFSET = 7;
/** Action-row tool targets (chat → SIEM → EDR → SOAR → NGFW), left-to-right centers. */
const ACTION_OPTION_CX = [168, 228, 288, 348, 408];
const ACTION_OPTION_LABELS = ["chat", "SIEM", "EDR", "SOAR", "NGFW"];
const ACTION_CHAT_CX = ACTION_OPTION_CX[0];
const ACTION_SIEM_CX = ACTION_OPTION_CX[1];
const ACTION_EDR_CX = ACTION_OPTION_CX[2];
const ACTION_SOAR_CX = ACTION_OPTION_CX[3];
const ACTION_NGFW_CX = ACTION_OPTION_CX[4];
const ACTION_OVAL_CX = ACTION_OPTION_CX[2];
const ACTION_OVAL_RX = 148;
const ROW_HEIGHTS = [
	R1_BOTTOM - Y1,
	R2_BOTTOM - Y2,
	R3_BOTTOM - Y3,
	R4_BOTTOM - Y4,
	R5_BOTTOM - Y5,
	R6_BOTTOM - Y6,
];
const SUM_ROW_H = ROW_HEIGHTS.reduce((a, b) => a + b, 0);

const USER_PHASE_COUNT = 6;

const REDUCE_MOTION_CAPTION =
	"Journey overview: distributed information enters The Security Information Exchange; explore it in the dashboard; integrate via REST, STIX, or MCP; act in your security tools; then share back into the Security Information Ecosystem.";

/** Keys rotate each full story loop (which source gets selected). */
const PICKUP_KEYS = ["isac", "vendor", "osint", "internal"];

/** Ecosystem: keep siem→node travel wall-clock at this length; hold at rest for `ECOSYSTEM_REST_MS`. */
const ECOSYSTEM_TRAVEL_MS = 3000;
const ECOSYSTEM_REST_MS = 6000;
const ECOSYSTEM_SEGMENT_MS = ECOSYSTEM_TRAVEL_MS + ECOSYSTEM_REST_MS;

/**
 * Wall-clock caption hold from copy length.
 * ~175 wpm with diagram distraction + settle buffer; soft-capped so dwells don't stall.
 */
function durationForCaption(caption, kind) {
	const words = caption.trim().split(/\s+/).filter(Boolean).length;
	const readMs = Math.round((words / 175) * 60_000) + 2000;
	const kindMin =
		kind === "ecosystem"
			? ECOSYSTEM_SEGMENT_MS
			: kind === "action_fan"
				? 4500
				: kind === "travel"
					? 3600
					: 3800;
	const softMax = kind === "ecosystem" ? Number.POSITIVE_INFINITY : 8500;
	return Math.min(softMax, Math.max(kindMin, readMs));
}

/**
 * @typedef {{ durationMs?: number, kind: string, userPhase: number, caption: string, from?: string, to?: string, at?: string }} StorySegmentDef
 */

/** @type {StorySegmentDef[]} */
const STORY_SEGMENT_DEFS = [
	{
		kind: "sources_intro",
		userPhase: 0,
		caption:
			"Step 1 — Distributed Information: threat data starts outside Truss, across vendors, ISACs, OSINT, and your own telemetry",
	},
	{
		kind: "source_pick",
		userPhase: 0,
		caption: "Next — those feeds are parsed, formatted, and preserved, then fed to the platform",
	},
	{
		kind: "travel",
		userPhase: 0,
		from: "pickup",
		to: "platform",
		caption: "That signal travels into Truss — one place for all known security knowledge",
	},
	{
		kind: "dwell",
		userPhase: 1,
		at: "platform",
		caption:
			"Step 2 — One Platform: The Truss Security Information Exchange is the gateway to the data. One platform for all your security data.",
	},
	{
		kind: "travel",
		userPhase: 2,
		from: "platform",
		to: "dashboard",
		caption: "Step 3 — Explore: open the dashboard to find and scope the intel you need",
	},
	{
		kind: "dwell",
		userPhase: 2,
		at: "dashboard",
		caption:
			"Use search, maps, and filters to understand the products you need before you pull them into your security stack",
	},
	{
		kind: "travel",
		userPhase: 3,
		from: "dashboard",
		to: "rest",
		caption: "Step 4 — Integrate: pull that same intel over REST into the workflows you already run",
	},
	{
		kind: "travel",
		userPhase: 3,
		from: "rest",
		to: "stix",
		caption: "Prefer structured data? Take STIX deliverables from the same record",
	},
	{
		kind: "travel",
		userPhase: 3,
		from: "stix",
		to: "mcp",
		caption:
			'Or ask Truss Local MCP Agent, via Cursor or Claude — e.g. "What are the latest threats that affect me?"',
	},
	{
		kind: "travel",
		userPhase: 4,
		from: "mcp",
		to: "siem",
		caption: "Step 5 — Action: land that intel where work happens. Push directly to your security stack endpoints",
	},
	{
		kind: "action_fan",
		userPhase: 4,
		caption: "The same signal can fan out to chat, SIEM, EDR, SOAR, and NGFW — one record, many tools",
	},
	{
		kind: "ecosystem",
		userPhase: 5,
		caption:
			"Step 6 — Security Information Ecosystem: share back so the exchange grows richer for everyone",
	},
];

const STORY_SEGMENTS = STORY_SEGMENT_DEFS.map((seg) => ({
	...seg,
	durationMs: durationForCaption(seg.caption, seg.kind),
}));

const PACED_STORY_SEGMENTS = STORY_SEGMENTS;

const TOTAL_STORY_MS = PACED_STORY_SEGMENTS.reduce((s, seg) => s + seg.durationMs, 0);

function computeRowDyEven(innerH) {
	const gEq = Math.max(0, (innerH - SUM_ROW_H) / 6);
	const dy = [];
	let T = 0;
	for (let i = 0; i < 6; i += 1) {
		dy[i] = T - [Y1, Y2, Y3, Y4, Y5, Y6][i];
		T += ROW_HEIGHTS[i] + gEq;
	}
	return dy;
}

function easeInOutCubic(t) {
	const x = Math.min(1, Math.max(0, t));
	return x < 0.5 ? 4 * x * x * x : 1 - (-2 * x + 2) ** 3 / 2;
}

function lerp2(a, b, t) {
	return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
}

/** Absolute SVG coordinates for story beats (rowDy from computeRowDyEven). */
function getStoryAnchors(rowDy) {
	const platformLeft = { x: 110, y: rowDy[1] + Y2 + 18 };
	const actionCy = rowDy[4] + Y5 + 15;
	return {
		vendor: { x: 128, y: rowDy[0] + Y1 + 18 },
		isac: { x: 168, y: rowDy[0] + Y1 + 12 },
		osint: { x: 168, y: rowDy[0] + Y1 + 26 },
		internal: { x: 208, y: rowDy[0] + Y1 + 18 },
		platform: platformLeft,
		platformLeft,
		dashboard: { x: 168, y: rowDy[2] + Y3 + 12 },
		rest: { x: 173, y: rowDy[3] + Y4 + 23 + INTEGRATE_DEST_Y_OFFSET },
		stix: { x: 227, y: rowDy[3] + Y4 + 23 + INTEGRATE_DEST_Y_OFFSET },
		mcp: { x: 299, y: rowDy[3] + Y4 + 23 + INTEGRATE_DEST_Y_OFFSET },
		chat: { x: ACTION_CHAT_CX, y: actionCy },
		siem: { x: ACTION_SIEM_CX, y: actionCy },
		edr: { x: ACTION_EDR_CX, y: actionCy },
		soar: { x: ACTION_SOAR_CX, y: actionCy },
		ngfw: { x: ACTION_NGFW_CX, y: actionCy },
		ecosystem: { x: 271, y: rowDy[5] + Y6 + 14 },
	};
}

const ROW_BY_KEY = {
	vendor: 0,
	isac: 0,
	osint: 0,
	internal: 0,
	platform: 1,
	dashboard: 2,
	rest: 3,
	stix: 3,
	mcp: 3,
	siem: 4,
	chat: 4,
	edr: 4,
	soar: 4,
	ngfw: 4,
	ecosystem: 5,
};

/**
 * @param {number} elapsedMs
 * @param {number[]} rowDy
 * @param {string} pickupKey
 */
function computeStoryFrame(elapsedMs, rowDy, pickupKey) {
	const anchors = getStoryAnchors(rowDy);
	const tLoop = ((elapsedMs % TOTAL_STORY_MS) + TOTAL_STORY_MS) % TOTAL_STORY_MS;
	let cum = 0;
	let seg = PACED_STORY_SEGMENTS[0];
	let localU = 0;
	for (let i = 0; i < STORY_SEGMENTS.length; i += 1) {
		const next = cum + PACED_STORY_SEGMENTS[i].durationMs;
		if (tLoop < next || i === PACED_STORY_SEGMENTS.length - 1) {
			seg = PACED_STORY_SEGMENTS[i];
			localU = (tLoop - cum) / seg.durationMs;
			break;
		}
		cum = next;
	}

	const resolve = (key) => {
		if (key === "pickup") return anchors[pickupKey] || anchors.isac;
		return anchors[key];
	};

	let marker = { x: anchors.platformLeft.x, y: anchors.platformLeft.y, visible: false, pulse: false };
	let travelFromRow = null;
	let travelToRow = null;
	let travelU = null;
	let dwellRow = null;
	let actionFanT = 0;
	let actionFanU = 0;

	if (seg.kind === "sources_intro") {
		marker.visible = false;
		dwellRow = 0;
	} else if (seg.kind === "source_pick") {
		const p = resolve("pickup");
		marker = { ...p, visible: true, pulse: true };
		dwellRow = 0;
	} else if (seg.kind === "travel") {
		const a = seg.from === "platform" ? anchors.platformLeft : resolve(seg.from);
		const b = seg.to === "platform" ? anchors.platformLeft : resolve(seg.to);
		const u = easeInOutCubic(localU);
		marker = { ...lerp2(a, b, u), visible: true, pulse: false };
		travelFromRow = ROW_BY_KEY[resolveTravelEndpointKey(seg.from, pickupKey)];
		travelToRow = ROW_BY_KEY[seg.to];
		travelU = u;
	} else if (seg.kind === "dwell") {
		if (seg.at === "platform") {
			marker = {
				...anchors.platformLeft,
				visible: true,
				pulse: localU > 0.82,
			};
		} else {
			const p = resolve(seg.at);
			marker = { ...p, visible: true, pulse: false };
		}
		dwellRow = ROW_BY_KEY[seg.at];
	} else if (seg.kind === "action_fan") {
		const p = anchors.siem;
		marker = { ...p, visible: true, pulse: false };
		dwellRow = 4;
		actionFanU = localU;
		actionFanT = easeInOutCubic(localU);
	} else if (seg.kind === "ecosystem") {
		const travelFrac = ECOSYSTEM_TRAVEL_MS / seg.durationMs;
		const moveU = Math.min(1, localU / travelFrac);
		const u = easeInOutCubic(moveU);
		const p0 = anchors.siem;
		const p1 = anchors.ecosystem;
		marker = { ...lerp2(p0, p1, u), visible: true, pulse: false };
	}

	const journey = buildCumulativeJourneyTrail(tLoop, rowDy, pickupKey);

	return {
		segment: seg,
		localU,
		marker,
		travelFromRow,
		travelToRow,
		travelU,
		dwellRow,
		actionFanT,
		userPhase: seg.userPhase,
		caption: seg.caption,
		pickupKey,
		cumulativeTrailD: journey.cumulativeTrailD,
		trailDots: journey.trailDots,
		actionFanU,
	};
}

function resolveTravelEndpointKey(fromKey, pickupKey) {
	if (fromKey === "pickup") return pickupKey;
	return fromKey;
}

/** Polyline + waypoint dots for every leg completed so far (and the in-progress leg). */
function buildCumulativeJourneyTrail(tLoop, rowDy, pickupKey) {
	const anchors = getStoryAnchors(rowDy);
	const resolve = (key) => {
		if (key === "pickup") return anchors[pickupKey] || anchors.isac;
		return anchors[key];
	};
	const cmds = [];
	const dots = [];
	let cum = 0;

	const travelEndpoint = (key) => {
		if (key === "platform") return anchors.platformLeft;
		return resolve(key);
	};

	const appendTravelFull = (fromKey, toKey) => {
		const a = travelEndpoint(fromKey);
		const b = travelEndpoint(toKey);
		if (!a || !b) return;
		if (cmds.length === 0) cmds.push(`M ${a.x} ${a.y}`);
		cmds.push(`L ${b.x} ${b.y}`);
		dots.push({ x: b.x, y: b.y });
	};

	const appendTravelPartial = (fromKey, toKey, u) => {
		const a = travelEndpoint(fromKey);
		const b = travelEndpoint(toKey);
		if (!a || !b) return;
		const m = lerp2(a, b, u);
		if (cmds.length === 0) cmds.push(`M ${a.x} ${a.y}`);
		cmds.push(`L ${m.x} ${m.y}`);
	};

	for (let i = 0; i < PACED_STORY_SEGMENTS.length; i += 1) {
		const s = PACED_STORY_SEGMENTS[i];
		const end = cum + s.durationMs;

		if (tLoop <= cum) break;

		if (s.kind === "travel") {
			if (tLoop >= end) {
				appendTravelFull(s.from, s.to);
				cum = end;
				continue;
			}
			const u = easeInOutCubic((tLoop - cum) / s.durationMs);
			appendTravelPartial(s.from, s.to, u);
			break;
		}

		if (s.kind === "ecosystem") {
			const p0 = anchors.siem;
			const p1 = anchors.ecosystem;
			const travelFrac = ECOSYSTEM_TRAVEL_MS / s.durationMs;
			if (tLoop >= end) {
				if (cmds.length === 0) cmds.push(`M ${p0.x} ${p0.y}`);
				cmds.push(`L ${p1.x} ${p1.y}`);
				dots.push({ x: p1.x, y: p1.y });
				cum = end;
				continue;
			}
			const w = (tLoop - cum) / s.durationMs;
			const moveU = Math.min(1, w / travelFrac);
			const u = easeInOutCubic(moveU);
			const m = lerp2(p0, p1, u);
			if (cmds.length === 0) cmds.push(`M ${p0.x} ${p0.y}`);
			cmds.push(`L ${m.x} ${m.y}`);
			break;
		}

		if (tLoop >= end) {
			cum = end;
			continue;
		}
		break;
	}

	const start = resolve("pickup");
	const trailDots = cmds.length > 0 && start ? [{ x: start.x, y: start.y }, ...dots] : [];

	return {
		cumulativeTrailD: cmds.length ? cmds.join(" ") : "",
		trailDots,
	};
}

function rowOpacityForStory(rowIndex, frame, reduceMotion) {
	if (reduceMotion) return 1;
	const dimFuture = 0.14;
	const { travelFromRow, travelToRow, travelU, dwellRow, segment, actionFanT } = frame;

	if (segment.kind === "ecosystem") {
		return 1;
	}
	if (travelFromRow != null && travelToRow != null && travelU != null) {
		if (rowIndex > travelToRow) return dimFuture;
		return 1;
	}
	if (dwellRow != null) {
		if (rowIndex > dwellRow) return dimFuture;
		return 1;
	}
	if (segment.kind === "sources_intro" || segment.kind === "source_pick") {
		if (rowIndex > 0) return dimFuture;
		return 1;
	}
	if (segment.kind === "action_fan") {
		if (rowIndex > 4) return dimFuture;
		if (rowIndex <= 3) return 1;
		return 0.88 + 0.12 * actionFanT;
	}
	return 1;
}

/**
 * Hero diagram: one signal travels distributed information → exchange → explore → REST/STIX/MCP → action → ecosystem.
 * Timeline-driven animation; `prefers-reduced-motion` shows a static overview.
 */
export default function IntentPathsIllustration() {
	const wrapRef = useRef(null);
	const diagramSlotRef = useRef(null);
	const svgRef = useRef(null);
	const rafRef = useRef(0);
	const startRef = useRef(0);
	const elapsedRef = useRef(0);
	const pickupRef = useRef("isac");
	const lastLoopRef = useRef(-1);

	const [innerH, setInnerH] = useState(VIEW_H);
	const [reduceMotion, setReduceMotion] = useState(false);
	const [caption, setCaption] = useState(STORY_SEGMENTS[0].caption);
	const [userPhase, setUserPhase] = useState(0);
	const [animTick, setAnimTick] = useState(0);

	useEffect(() => {
		const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
		const apply = () => setReduceMotion(mq.matches);
		apply();
		mq.addEventListener("change", apply);
		return () => mq.removeEventListener("change", apply);
	}, []);

	useLayoutEffect(() => {
		const slot = diagramSlotRef.current;
		if (!slot) return undefined;

		/* Measure the diagram slot only (excludes caption) so row gaps match painted space.
		 * Slot height is fixed by the hero panel flex layout — no viewBox feedback loop. */
		const update = () => {
			const { width, height } = slot.getBoundingClientRect();
			if (width <= 0 || height <= 0) return;
			const isStackedMobile = !window.matchMedia("(min-width: 900px)").matches;
			const next = isStackedMobile ? VIEW_H : Math.max(VIEW_H, (520 * height) / width);
			setInnerH((prev) => (Math.abs(prev - next) < 0.5 ? prev : next));
		};

		update();
		const ro = new ResizeObserver(update);
		ro.observe(slot);
		return () => ro.disconnect();
	}, []);

	const rowDy = computeRowDyEven(innerH);
	const rowDyRef = useRef(rowDy);
	rowDyRef.current = rowDy;

	useEffect(() => {
		if (reduceMotion) return undefined;

		startRef.current = performance.now() - elapsedRef.current;
		let captionLast = PACED_STORY_SEGMENTS[0].caption;
		let phaseLast = 0;

		const tick = (now) => {
			const elapsed = now - startRef.current;
			elapsedRef.current = elapsed;
			const loopIdx = Math.floor(elapsed / TOTAL_STORY_MS);
			if (loopIdx !== lastLoopRef.current) {
				lastLoopRef.current = loopIdx;
				pickupRef.current = PICKUP_KEYS[loopIdx % PICKUP_KEYS.length];
			}

			const frame = computeStoryFrame(elapsed, rowDyRef.current, pickupRef.current);

			if (frame.caption !== captionLast) {
				captionLast = frame.caption;
				setCaption(frame.caption);
			}
			if (frame.userPhase !== phaseLast) {
				phaseLast = frame.userPhase;
				setUserPhase(frame.userPhase);
			}

			/* Every frame: path, waypoint dots, and marker all recompute from the same rowDy. */
			setAnimTick((x) => x + 1);

			rafRef.current = requestAnimationFrame(tick);
		};

		rafRef.current = requestAnimationFrame(tick);
		return () => cancelAnimationFrame(rafRef.current);
	}, [reduceMotion]);

	/* animTick forces ~60fps recomputation; startRef is the mutable story clock. */
	const frameForRender = useMemo(() => {
		if (reduceMotion || typeof performance === "undefined") return null;
		return computeStoryFrame(performance.now() - startRef.current, rowDy, pickupRef.current);
	}, [reduceMotion, rowDy, animTick]); // eslint-disable-line react-hooks/exhaustive-deps

	const marker = frameForRender?.marker;
	const rowDim = (i) => {
		if (reduceMotion) return 1;
		if (!frameForRender) return 1;
		return rowOpacityForStory(i, frameForRender, false);
	};

	const actionFanU = !reduceMotion && frameForRender?.segment.kind === "action_fan" ? frameForRender.actionFanU ?? 0 : 0;
	const actionSegKind = frameForRender?.segment.kind;
	const showActionOval = !reduceMotion && (actionSegKind === "action_fan" || actionSegKind === "ecosystem");
	let actionOvalOpacity = 0;
	if (showActionOval && frameForRender) {
		if (actionSegKind === "action_fan") {
			actionOvalOpacity = actionFanU < 0.22 ? (actionFanU / 0.22) * 0.82 : 0.82;
		} else {
			const uEco = frameForRender.localU;
			if (uEco >= 0.88) actionOvalOpacity = 0.82 * Math.max(0, 1 - (uEco - 0.88) / 0.12);
			else actionOvalOpacity = 0.82;
		}
	}
	const actionFlashSlot = (i) => {
		const slot0 = 0.26;
		const dur = 0.11;
		const gap = 0.03;
		const start = slot0 + i * (dur + gap);
		const s = (actionFanU - start) / dur;
		if (s <= 0 || s >= 1) return { r: 0, opacity: 0 };
		return { r: 4 + 9 * s, opacity: 0.58 * (1 - s * 0.55) };
	};

	return (
		<Wrap ref={wrapRef} aria-hidden>
			<DiagramSlot ref={diagramSlotRef}>
				<svg
					ref={svgRef}
					viewBox={`0 0 520 ${innerH}`}
					preserveAspectRatio="xMidYMin meet"
					xmlns="http://www.w3.org/2000/svg"
					focusable="false"
				>
				<defs>
					<linearGradient id="intentPathGrad" x1="0%" y1="0%" x2="100%" y2="0%">
						<stop offset="0%" stopColor="#0077cc" stopOpacity="0.95" />
						<stop offset="100%" stopColor="#ffc400" stopOpacity="0.45" />
					</linearGradient>
				</defs>

				<g className="hero-scene-row" style={{ opacity: rowDim(0) }} transform={`translate(0, ${rowDy[0]})`}>
					<rect x="4" y={Y1} width="100" height="36" rx="6" fill="rgba(0,119,204,0.18)" stroke="#0077cc" strokeWidth="1" />
					<text x="54" y={Y1 + 11} textAnchor="middle" fill="#edf2f6" fontSize="8" fontFamily="system-ui, sans-serif" fontWeight="700">
						Distributed
					</text>
					<text x="54" y={Y1 + 20} textAnchor="middle" fill="#edf2f6" fontSize="8" fontFamily="system-ui, sans-serif" fontWeight="700">
						Information
					</text>
					<text x="54" y={Y1 + 31} textAnchor="middle" fill="#9bb6d4" fontSize="6.5" fontFamily="system-ui, sans-serif">
						Decentralized sources
					</text>
					<circle cx="128" cy={Y1 + 18} r="7" fill="rgba(237,242,246,0.1)" stroke="#6f839f" strokeWidth="1" />
					<text x="128" y={Y1 + 21} textAnchor="middle" fill="#c5d4e0" fontSize="6" fontFamily="system-ui, sans-serif">
						vendor
					</text>
					<circle cx="168" cy={Y1 + 12} r="7" fill="rgba(237,242,246,0.1)" stroke="#6f839f" strokeWidth="1" />
					<text x="168" y={Y1 + 15} textAnchor="middle" fill="#c5d4e0" fontSize="6" fontFamily="system-ui, sans-serif">
						ISAC
					</text>
					<circle cx="168" cy={Y1 + 26} r="7" fill="rgba(237,242,246,0.1)" stroke="#6f839f" strokeWidth="1" />
					<text x="168" y={Y1 + 29} textAnchor="middle" fill="#c5d4e0" fontSize="6" fontFamily="system-ui, sans-serif">
						OSINT
					</text>
					<circle cx="208" cy={Y1 + 18} r="7" fill="rgba(237,242,246,0.1)" stroke="#6f839f" strokeWidth="1" />
					<text x="208" y={Y1 + 21} textAnchor="middle" fill="#c5d4e0" fontSize="6" fontFamily="system-ui, sans-serif">
						internal
					</text>
					<path
						className="flow-line"
						d={`M 223 ${Y1 + 18} H 250`}
						fill="none"
						stroke="url(#intentPathGrad)"
						strokeWidth="1.75"
						strokeLinecap="round"
					/>
					<path d={`M 250 ${Y1 + 12} L 250 ${Y1 + 24} L 256 ${Y1 + 18} Z`} fill="#6f839f" />
					<text x="258" y={Y1 + 16} fill="#9bb6d4" fontSize="7" fontFamily="system-ui, sans-serif">
						Issue: Many producers, formats, styles
					</text>
					<text x="258" y={Y1 + 27} fill="#6f839f" fontSize="7" fontFamily="system-ui, sans-serif">
						Result: Overlapping and Lost Coverage
					</text>
				</g>

				<g className="hero-scene-row" style={{ opacity: rowDim(1) }} transform={`translate(0, ${rowDy[1]})`}>
					<rect x="4" y={Y2} width="100" height="36" rx="6" fill="rgba(255,196,0,0.12)" stroke="#ffc400" strokeWidth="1" />
					<text x="54" y={Y2 + 14} textAnchor="middle" fill="#edf2f6" fontSize="9" fontFamily="system-ui, sans-serif" fontWeight="700">
						One platform
					</text>
					<text x="54" y={Y2 + 26} textAnchor="middle" fill="#9bb6d4" fontSize="6.5" fontFamily="system-ui, sans-serif">
						Converged TI
					</text>
					<rect x="118" y={Y2 - 2} width="396" height="40" rx="8" fill="rgba(0,119,204,0.22)" stroke="#0077cc" strokeWidth="1.25" />
					<text x="316" y={Y2 + 15} textAnchor="middle" fill="#edf2f6" fontSize="9" fontFamily="system-ui, sans-serif" fontWeight="700">
						The Security Information Exchange
					</text>
					<text x="316" y={Y2 + 30} textAnchor="middle" fill="#c5d4e0" fontSize="7.5" fontFamily="system-ui, sans-serif">
						Search, govern, and deliver — one product surface
					</text>
				</g>

				<g className="hero-scene-row" style={{ opacity: rowDim(2) }} transform={`translate(0, ${rowDy[2]})`}>
					<rect x="4" y={Y3} width="100" height="36" rx="6" fill="rgba(0,119,204,0.2)" stroke="#0077cc" strokeWidth="1" />
					<text x="54" y={Y3 + 14} textAnchor="middle" fill="#edf2f6" fontSize="9" fontFamily="system-ui, sans-serif" fontWeight="700">
						Explore
					</text>
					<text x="54" y={Y3 + 26} textAnchor="middle" fill="#9bb6d4" fontSize="7" fontFamily="system-ui, sans-serif">
						Dashboard
					</text>
					<rect x="118" y={Y3 - 4} width="108" height="52" rx="6" fill="none" stroke="#586d8e" strokeWidth="1.25" />
					<rect x="126" y={Y3 + 2} width="92" height="20" rx="3" fill="rgba(237,242,246,0.08)" stroke="#6f839f" strokeWidth="0.75" />
					<line x1="126" y1={Y3 + 30} x2="168" y2={Y3 + 30} stroke="#0077cc" strokeWidth="2" strokeLinecap="round" opacity="0.85" />
					<line x1="126" y1={Y3 + 36} x2="194" y2={Y3 + 36} stroke="#586d8e" strokeWidth="1.5" strokeLinecap="round" />
					<line x1="126" y1={Y3 + 42} x2="184" y2={Y3 + 42} stroke="#586d8e" strokeWidth="1.5" strokeLinecap="round" />
					<path
						className="flow-line"
						d={`M 227 ${Y3 + 18} H 241`}
						fill="none"
						stroke="url(#intentPathGrad)"
						strokeWidth="1.75"
						strokeLinecap="round"
					/>
					<path d={`M 241 ${Y3 + 12} L 241 ${Y3 + 24} L 247 ${Y3 + 18} Z`} fill="#6f839f" />
					<text x="250" y={Y3 + 20} fill="#9bb6d4" fontSize="8" fontFamily="system-ui, sans-serif">
						Find and scope the intel you need
					</text>
				</g>

				<g className="hero-scene-row" style={{ opacity: rowDim(3) }} transform={`translate(0, ${rowDy[3]})`}>
					<rect x="4" y={Y4} width="100" height="36" rx="6" fill="rgba(0,119,204,0.15)" stroke="#0077cc" strokeWidth="1" />
					<text x="54" y={Y4 + 14} textAnchor="middle" fill="#edf2f6" fontSize="9" fontFamily="system-ui, sans-serif" fontWeight="700">
						Integrate
					</text>
					<text x="54" y={Y4 + 26} textAnchor="middle" fill="#9bb6d4" fontSize="6" fontFamily="system-ui, sans-serif">
						Deliverables
					</text>
					<text x="118" y={Y4 + 22} fill="#6f839f" fontSize="16" fontFamily="Consolas, monospace" fontWeight="700">
						{"</>"}
					</text>
					<rect x="148" y={Y4 + 9} width="50" height="18" rx="4" fill="rgba(255,196,0,0.12)" stroke="#ffc400" strokeWidth="0.9" />
					<text x="173" y={Y4 + 21} textAnchor="middle" fill="#edf2f6" fontSize="8.5" fontFamily="system-ui, sans-serif" fontWeight="600">
						REST
					</text>
					<rect x="204" y={Y4 + 9} width="46" height="18" rx="4" fill="rgba(0,119,204,0.18)" stroke="#4da3e0" strokeWidth="0.9" />
					<text x="227" y={Y4 + 21} textAnchor="middle" fill="#edf2f6" fontSize="8.5" fontFamily="system-ui, sans-serif" fontWeight="600">
						STIX
					</text>
					<rect x="256" y={Y4 + 9} width="86" height="18" rx="4" fill="rgba(237,242,246,0.06)" stroke="#6f839f" strokeWidth="0.9" />
					<text x="299" y={Y4 + 21} textAnchor="middle" fill="#c5d4e0" fontSize="8.5" fontFamily="system-ui, sans-serif" fontWeight="600">
						MCP
					</text>
					<text x="352" y={Y4 + 15} fill="#9bb6d4" fontSize="7" fontFamily="system-ui, sans-serif">
						Ask in your agent, or
					</text>
					<text x="352" y={Y4 + 25} fill="#6f839f" fontSize="7" fontFamily="system-ui, sans-serif">
						pull REST / STIX
					</text>
				</g>

				<g className="hero-scene-row" style={{ opacity: rowDim(4) }} transform={`translate(0, ${rowDy[4]})`}>
					<rect
						x="4"
						y={Y5 + ACTION_BOX_Y_OFFSET}
						width="100"
						height="36"
						rx="6"
						fill="rgba(0,119,204,0.15)"
						stroke="#0077cc"
						strokeWidth="1"
					/>
					<text
						x="54"
						y={Y5 + ACTION_BOX_Y_OFFSET + 16}
						textAnchor="middle"
						fill="#edf2f6"
						fontSize="9"
						fontFamily="system-ui, sans-serif"
						fontWeight="700"
					>
						Action
					</text>
					<text
						x="54"
						y={Y5 + ACTION_BOX_Y_OFFSET + 28}
						textAnchor="middle"
						fill="#9bb6d4"
						fontSize="7"
						fontFamily="system-ui, sans-serif"
					>
						Your tools
					</text>
					<text x="118" y={Y5 + 37} fill="#9bb6d4" fontSize="7" fontFamily="system-ui, sans-serif">
						Push the same intel into the tools you already run
					</text>
					{ACTION_OPTION_LABELS.map((label, i) => (
						<g key={label}>
							<circle
								cx={ACTION_OPTION_CX[i]}
								cy={Y5 + 15}
								r="9"
								fill="rgba(237,242,246,0.1)"
								stroke="#6f839f"
								strokeWidth="1"
							/>
							<text
								x={ACTION_OPTION_CX[i]}
								y={Y5 + 18}
								textAnchor="middle"
								fill="#c5d4e0"
								fontSize="6"
								fontFamily="system-ui, sans-serif"
							>
								{label}
							</text>
						</g>
					))}
					{showActionOval ? (
						<ellipse
							cx={ACTION_OVAL_CX}
							cy={Y5 + 15}
							rx={ACTION_OVAL_RX}
							ry="14"
							fill="none"
							stroke="#ffc400"
							strokeWidth="1.35"
							opacity={actionOvalOpacity}
						/>
					) : null}
					{!reduceMotion && frameForRender?.segment.kind === "action_fan"
						? ACTION_OPTION_CX.map((cx, i) => {
								const { r, opacity } = actionFlashSlot(i);
								if (r <= 0) return null;
								return (
									<circle
										key={`action-flash-${String(i)}`}
										cx={cx}
										cy={Y5 + 15}
										r={r}
										fill="none"
										stroke={i % 2 === 0 ? "#ffc400" : "#0077cc"}
										strokeWidth="1.2"
										opacity={opacity}
									/>
								);
							})
						: null}
				</g>

				<g className="hero-scene-row" style={{ opacity: rowDim(5) }} transform={`translate(0, ${rowDy[5]})`}>
					<rect x="4" y={Y6} width="100" height="28" rx="6" fill="rgba(255,196,0,0.1)" stroke="#ffc400" strokeWidth="1" />
					<text x="54" y={Y6 + 12} textAnchor="middle" fill="#edf2f6" fontSize="7" fontFamily="system-ui, sans-serif" fontWeight="700">
						Security Information
					</text>
					<text x="54" y={Y6 + 22} textAnchor="middle" fill="#edf2f6" fontSize="8" fontFamily="system-ui, sans-serif" fontWeight="700">
						Ecosystem
					</text>
					<rect x="118" y={Y6} width="56" height="28" rx="5" fill="rgba(0,119,204,0.14)" stroke="#0077cc" strokeWidth="1" />
					<text x="146" y={Y6 + 14} textAnchor="middle" fill="#edf2f6" fontSize="7" fontFamily="system-ui, sans-serif" fontWeight="600">
						Upload
					</text>
					<text x="146" y={Y6 + 23} textAnchor="middle" fill="#9bb6d4" fontSize="6" fontFamily="system-ui, sans-serif">
						share back
					</text>
					<circle cx="210" cy={Y6 + 11} r="5.5" fill="#0077cc" opacity="0.88" />
					<circle cx="238" cy={Y6 + 5} r="5.5" fill="#ffc400" opacity="0.78" />
					<circle cx="268" cy={Y6 + 14} r="5.5" fill="#0077cc" opacity="0.76" />
					<circle cx="216" cy={Y6 + 26} r="5.5" fill="#6f839f" opacity="0.9" />
					<circle cx="250" cy={Y6 + 28} r="5.5" fill="#0077cc" opacity="0.72" />
					<circle cx="284" cy={Y6 + 20} r="5.5" fill="#ffc400" opacity="0.68" />
					<line x1="215" y1={Y6 + 11} x2="233" y2={Y6 + 6} stroke="#586d8e" strokeWidth="1" strokeLinecap="round" />
					<line x1="243" y1={Y6 + 6} x2="263" y2={Y6 + 12} stroke="#586d8e" strokeWidth="1" strokeLinecap="round" />
					<line x1="212" y1={Y6 + 15} x2="218" y2={Y6 + 23} stroke="#586d8e" strokeWidth="1" strokeLinecap="round" />
					<line x1="224" y1={Y6 + 27} x2="244" y2={Y6 + 28} stroke="#586d8e" strokeWidth="1" strokeLinecap="round" />
					<line x1="256" y1={Y6 + 28} x2="278" y2={Y6 + 22} stroke="#586d8e" strokeWidth="1" strokeLinecap="round" />
					<line x1="265" y1={Y6 + 16} x2="280" y2={Y6 + 19} stroke="#586d8e" strokeWidth="0.95" strokeLinecap="round" opacity="0.9" />
					<line x1="238" y1={Y6 + 10} x2="224" y2={Y6 + 24} stroke="#586d8e" strokeWidth="0.9" strokeLinecap="round" opacity="0.85" />
					<line x1="242" y1={Y6 + 9} x2="254" y2={Y6 + 26} stroke="#586d8e" strokeWidth="0.9" strokeLinecap="round" opacity="0.82" />
					<line x1="268" y1={Y6 + 18} x2="258" y2={Y6 + 26} stroke="#586d8e" strokeWidth="0.9" strokeLinecap="round" opacity="0.8" />
					<path
						className="flow-line"
						d={`M 290 ${Y6 + 18} H 320`}
						fill="none"
						stroke="url(#intentPathGrad)"
						strokeWidth="1.75"
						strokeLinecap="round"
					/>
					<path d={`M 320 ${Y6 + 12} L 320 ${Y6 + 24} L 326 ${Y6 + 18} Z`} fill="#6f839f" />
					<text x="330" y={Y6 + 12} fill="#9bb6d4" fontSize="8" fontFamily="system-ui, sans-serif">
						Decentralized security
					</text>
					<text x="330" y={Y6 + 24} fill="#6f839f" fontSize="7" fontFamily="system-ui, sans-serif">
						Your data helps the community
					</text>
				</g>

				<g style={{ pointerEvents: "none" }}>
					{!reduceMotion && frameForRender?.cumulativeTrailD ? (
						<path
							d={frameForRender.cumulativeTrailD}
							fill="none"
							stroke="url(#intentPathGrad)"
							strokeWidth="2.25"
							strokeLinecap="round"
							strokeLinejoin="round"
							opacity="0.52"
						/>
					) : null}
					{!reduceMotion && frameForRender?.trailDots?.length
						? frameForRender.trailDots.map((p, idx, arr) => {
								const last = idx === arr.length - 1;
								const { x: mx, y: my, visible: mv } = frameForRender.marker;
								const overlapsMarker =
									last &&
									arr.length > 1 &&
									mv &&
									Math.hypot(p.x - mx, p.y - my) < 3.5;
								if (overlapsMarker) return null;
								return (
									<circle
										key={`trail-dot-${String(idx)}`}
										cx={p.x}
										cy={p.y}
										r={idx === 0 ? 3.25 : 3.75}
										fill={idx === 0 ? "rgba(237,242,246,0.95)" : "rgba(77,163,224,0.9)"}
										stroke="#0077cc"
										strokeWidth="1.1"
										opacity="0.88"
									/>
								);
							})
						: null}
					{marker?.visible ? (
						<>
							<circle
								cx={marker.x}
								cy={marker.y}
								r="10"
								fill="#ffc400"
								opacity={marker.pulse ? 0.55 : 0.25}
								className={marker.pulse ? "story-marker-pulse" : undefined}
							/>
							<circle
								cx={marker.x}
								cy={marker.y}
								r="5"
								fill="#edf2f6"
								stroke="#0077cc"
								strokeWidth="1.5"
								opacity="1"
							/>
						</>
					) : null}
				</g>
			</svg>
			</DiagramSlot>
			<CaptionBar>
				<Caption aria-live={reduceMotion ? undefined : "polite"}>
					{reduceMotion ? REDUCE_MOTION_CAPTION : caption}
				</Caption>
				{!reduceMotion ? (
					<SceneDots aria-hidden>
						{Array.from({ length: USER_PHASE_COUNT }, (_, i) => (
							<SceneDot key={String(i)} $on={i === userPhase} />
						))}
					</SceneDots>
				) : null}
			</CaptionBar>
		</Wrap>
	);
}
